/**
 * EmailBulkToolbar — shown when selectedIds.length > 0.
 * Reads selectedIds from store and dispatches PATCH /api/email/v1/bulk.
 */

import { h } from 'preact'

export function EmailBulkToolbar({ store, i18n }) {
  const count = store.selectionCount.value
  const selectedIds = store.selectedIds.value
  const activeMailbox = store.activeMailbox.value
  const selectedRows = (store.emails.value || []).filter(e => selectedIds.includes(e.id))
  const selectedMailboxIds = [...new Set(selectedRows.map(e => e.mailbox_id).filter(id => id != null))]
  // Move targets must match every selected email's mailbox (single-mailbox selection only).
  const bulkMailboxId = selectedMailboxIds.length === 1
    ? selectedMailboxIds[0]
    : (activeMailbox ?? null)
  const emailTags = (store.emailTags.value || []).filter(t =>
    bulkMailboxId == null ? false : Number(t.mailbox_id) === Number(bulkMailboxId)
  )
  const emailFolders = (store.emailFolders.value || []).filter(f =>
    bulkMailboxId == null ? false : Number(f.mailbox_id) === Number(bulkMailboxId)
  )
  const activeFolder = store.activeFolder.value
  const showRestore = activeFolder === 'archived' || activeFolder === 'trash' || activeFolder === 'spam'
  const isSpamFolder = activeFolder === 'spam'

  async function bulk(action, extra = {}) {
    await store.bulkUpdate(selectedIds, action, extra)
  }

  return (
    <div class="email-bulk-toolbar d-flex align-items-center gap-1 px-2 py-1 bg-primary-subtle border-bottom flex-wrap">

      {/* Selection count + clear */}
      <div class="d-flex align-items-center gap-2 me-2">
        <button
          class="btn btn-sm btn-link text-primary p-0"
          title={i18n?.t('helpers.actions.cancel')}
          onClick={store.clearSelection}
        >
          <i class="bi bi-x-lg" />
        </button>
        <span class="small fw-semibold text-primary">{count}</span>
      </div>

      {/* Bulk actions */}
      <button
        class="btn btn-sm btn-outline-secondary"
        title={i18n?.t('emails.mark_read')}
        onClick={() => bulk('read')}
      >
        <i class="bi bi-envelope-open" />
      </button>

      <button
        class="btn btn-sm btn-outline-secondary"
        title={i18n?.t('emails.mark_unread')}
        onClick={() => bulk('unread')}
      >
        <i class="bi bi-envelope" />
      </button>

      {showRestore ? (
        <button
          class="btn btn-sm btn-outline-success"
          title={i18n?.t(isSpamFolder ? 'emails.not_spam' : 'emails.restore_to_inbox')}
          onClick={() => bulk(isSpamFolder ? 'not_spam' : 'restore')}
        >
          <i class="bi bi-inbox" />
        </button>
      ) : (
        <>
          <button
            class="btn btn-sm btn-outline-secondary"
            title={i18n?.t('emails.archive')}
            onClick={() => bulk('archive')}
          >
            <i class="bi bi-archive" />
          </button>

          <button
            class="btn btn-sm btn-outline-secondary"
            title={i18n?.t('emails.mark_spam')}
            onClick={() => bulk('spam')}
          >
            <i class="bi bi-exclamation-octagon" />
          </button>

          <button
            class="btn btn-sm btn-outline-secondary"
            title={i18n?.t('emails.trash')}
            onClick={() => bulk('trash')}
          >
            <i class="bi bi-trash" />
          </button>
        </>
      )}

      <button
        class="btn btn-sm btn-outline-warning"
        title={i18n?.t('emails.star')}
        onClick={() => bulk('star')}
      >
        <i class="bi bi-star" />
      </button>

      {/* Move to folder */}
      <div class="dropdown">
        <button
          class="btn btn-sm btn-outline-secondary dropdown-toggle"
          data-bs-toggle="dropdown"
          data-bs-strategy="fixed"
          title={i18n?.t('emails.move_to_folder')}
        >
          <i class="bi bi-folder-symlink" />
        </button>
        <ul class="dropdown-menu shadow-sm">
          <li>
            <button
              class="dropdown-item small d-flex align-items-center gap-2"
              type="button"
              onClick={() => bulk('move_to_folder', { email_folder_id: '' })}
            >
              <i class="bi bi-inbox" />
              {i18n?.t('emails.move_to_inbox')}
            </button>
          </li>
          {emailFolders.length > 0 && <li><hr class="dropdown-divider my-1" /></li>}
          {emailFolders.map(folder => (
            <li key={folder.id}>
              <button
                class="dropdown-item small d-flex align-items-center gap-2"
                type="button"
                onClick={() => bulk('move_to_folder', { email_folder_id: folder.id })}
              >
                <i class="bi bi-folder" style={folder.color ? { color: folder.color } : undefined} />
                {folder.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Tag add/remove dropdown */}
      {emailTags.length > 0 && (
        <div class="dropdown">
          <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" data-bs-strategy="fixed" data-bs-auto-close="outside">
            <i class="bi bi-tag" />
          </button>
          <div class="dropdown-menu shadow-sm p-2" style="min-width:200px">
            <div class="small text-muted fw-semibold mb-1 px-2">{i18n?.t('emails.add_tag')}</div>
            {emailTags.map(tag => (
              <button
                key={`add-${tag.id}`}
                class="dropdown-item small d-flex align-items-center gap-2"
                onClick={() => bulk('tag_add', { tag_id: tag.id })}
              >
                <span class="badge rounded-pill" style={{ backgroundColor: tag.color || '#6c757d', fontSize: '0.7rem' }}>
                  {tag.tag}
                </span>
              </button>
            ))}
            <hr class="my-1" />
            <div class="small text-muted fw-semibold mb-1 px-2">{i18n?.t('emails.remove_tag')}</div>
            {emailTags.map(tag => (
              <button
                key={`rm-${tag.id}`}
                class="dropdown-item small d-flex align-items-center gap-2"
                onClick={() => bulk('tag_remove', { tag_id: tag.id })}
              >
                <span class="badge rounded-pill" style={{ backgroundColor: tag.color || '#6c757d', fontSize: '0.7rem' }}>
                  {tag.tag}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Select all */}
      <button
        class="btn btn-sm btn-link text-primary p-0 ms-auto"
        onClick={() => store.selectAll(store.emails.value.map(e => e.id))}
      >
        {i18n?.t('emails.select_all')}
      </button>

    </div>
  )
}
